import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileText, Check, Download, Eye, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { DocumentUploadButton } from '@/components/documents/DocumentUploadButton';
import { supabase } from '@/integrations/supabase/client';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { FinalizeWithSignatures } from '@/components/documents/FinalizeWithSignatures';
import DocumentEmailWizard from '@/components/documents/DocumentEmailWizard';
import { getRequiredDocuments } from '@/utils/documents/contributingAssets';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { embedSignaturesIntoHtml } from '@/lib/signatureEmbedding';
import { DocumentSigningControl } from '../DocumentSigningControl';
import { SealApplyCheckbox } from '../SealApplyCheckbox';
import { useAssociationSeal } from '../useAssociationSeal';
import { generatedFilePath } from '../docProvenance';
import { finishLegacyHtml } from '../legacyDocumentFinish';
import { legacySigningMeta } from '../legacySigningSpecs';
import { downloadDocumentById } from '@/lib/downloadDocumentById';

interface AssetTransfersPhase1Props {
  task: any;
  phaseData: any;
  onPhaseComplete: () => void;
}

/**
 * Generate dynamic board member signature blocks based on UNA membership
 */
function generateBoardMemberSignatureBlocks(unaMembers: any[]): string {
  if (!unaMembers || unaMembers.length === 0) return '';
  
  // Filter active board members - include any active member with board/member role
  const boardMembers = unaMembers.filter((m: any) => {
    const isActive = m.status === 'active';
    const roleNormalized = (m.role || m.member_role || '').toLowerCase().replace(/\s+/g, '_');
    const isBoard = roleNormalized.includes('board') || 
                   roleNormalized.includes('member') ||
                   roleNormalized === 'advisor' ||
                   roleNormalized === 'observer';
    return isActive && isBoard;
  });
  
  if (boardMembers.length === 0) return '';
  
  const blocks = boardMembers.map((member: any) => `
    <div class="signature-block" style="margin-bottom: 0.4in; padding: 0.3in; border: 1px solid #ddd; border-radius: 4px; background-color: #fafafa; page-break-inside: avoid;">
      <div class="signature-line" data-signer-id="${member.id}" data-signature-role="board_member" style="border-bottom: 2px solid #333; width: 3.5in; min-height: 0.6in; margin-bottom: 0.15in; background-color: white;"></div>
      <div class="signature-label" style="font-size: 10pt; margin-top: 0.1in;">${member.full_name}, Board Member</div>
      <div style="margin-top: 0.15in;">Date: _____________</div>
    </div>
  `).join('\n');
  
  return `
    <div style="margin-top: 40px; page-break-inside: avoid;">
      <h3 style="font-size: 14pt; margin-bottom: 0.5in; border-bottom: 2px solid #333; padding-bottom: 0.15in;">Board Member Approval</h3>
      <p style="margin-bottom: 0.3in; font-style: italic; font-size: 11pt;">
        The undersigned Board Members hereby approve this Resolution:
      </p>
      ${blocks}
    </div>
  `;
}

export function AssetTransfersPhase1({ task, phaseData, onPhaseComplete }: AssetTransfersPhase1Props) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const seal = useAssociationSeal(task.una_id);
  const [generating, setGenerating] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<any | null>(null);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [unaData, setUnaData] = useState<any | null>(null);
  const [signatureWizardOpen, setSignatureWizardOpen] = useState(false);
  const [selectedDocForSignature, setSelectedDocForSignature] = useState<any | null>(null);
  const [emailWizardOpen, setEmailWizardOpen] = useState(false);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState<string | null>(null);

  const workflowData = task.workflow_data || {};
  const assetType = workflowData.asset_type || '';

  // Get required documents for this asset type
  const requiredDocuments = getRequiredDocuments(assetType, 1);

  useEffect(() => {
    loadDocuments();
    loadUnaData();
  }, [task.id]);

  async function loadUnaData() {
    if (!task.una_id) return;
    
    const { data, error } = await supabase
      .from('unas')
      .select(`
        *,
        una_members(
          id,
          user_id,
          full_name,
          email,
          role,
          member_role,
          status
        )
      `)
      .eq('id', task.una_id)
      .single();

    if (!error && data) {
      // Fetch Secretary Treasurer profile from profiles table using treasury_secretary_id
      let treasurySecretaryName = null;
      let treasurySecretaryEmail = null;
      
      if (data.treasury_secretary_id) {
        const { data: tsProfile } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .eq('id', data.treasury_secretary_id)
          .maybeSingle();
        
        if (tsProfile) {
          treasurySecretaryName = tsProfile.full_name;
          treasurySecretaryEmail = tsProfile.email;
        }
      }
      
      // Extend the data object with treasury secretary info
      setUnaData({
        ...data,
        treasury_secretary_name: treasurySecretaryName,
        treasury_secretary_email: treasurySecretaryEmail
      });
    }
  }

  async function loadDocuments() {
    const { data, error } = await supabase
      .from('generated_documents')
      .select('*')
      .eq('task_id', task.id)
      .order('generated_at', { ascending: true }) as { data: any[] | null; error: any };

    if (error) {
      console.error('Error loading documents:', error);
      return;
    }

    // Filter to phase 1 documents by checking merge_field_data
    const phase1Docs = (data || []).filter(d => {
      const templateKey = d.merge_field_data?.template_key || '';
      return templateKey && (
        templateKey.includes('authorization') ||
        templateKey.includes('contribution_summary') ||
        templateKey.includes('vehicle_instrument') ||
        templateKey.includes('private_assignment') ||
        templateKey.includes('ip_assignment')
      );
    });

    setDocuments(phase1Docs);
  }

  async function handleGenerateDocument(docReq: any) {
    setGenerating(docReq.key);
    
    try {
      let htmlContent = '';

      // Import the generator function dynamically based on asset type
      const { 
        generateAuthorizationResolutionHtml: genAuth,
        generateContributionSummaryHtml: genSummary,
        generateVehicleInstrumentHtml: genVehicle,
        generatePrivateAssignmentHtml: genPrivate,
        generateIPAssignmentHtml: genIP
      } = await import('@/utils/documents/contributingAssets');

      // Extend workflowData with treasury_secretary_id for signature embedding
      const extendedWorkflowData = {
        ...workflowData,
        treasury_secretary_id: unaData?.treasury_secretary_id || '',
        treasury_secretary_name: unaData?.treasury_secretary_name || '',
      };

      // Generate document based on key
      switch (docReq.key) {
        case 'authorization_resolution':
          htmlContent = genAuth(extendedWorkflowData, unaData);
          break;
        case 'contribution_summary':
          htmlContent = genSummary(extendedWorkflowData, unaData);
          break;
        case 'vehicle_instrument':
          if (assetType.includes('Vehicle')) {
            htmlContent = genVehicle(extendedWorkflowData, unaData);
          }
          break;
        case 'private_assignment':
          if (assetType.includes('Real Estate') || assetType.includes('Business Entity')) {
            htmlContent = genPrivate(extendedWorkflowData, unaData);
          }
          break;
        case 'ip_assignment':
          if (assetType === 'Intellectual Property') {
            htmlContent = genIP(extendedWorkflowData, unaData);
          }
          break;
        default:
          throw new Error(`Document generator not yet implemented: ${docReq.key}`);
      }

      if (!htmlContent) {
        throw new Error('Failed to generate document content');
      }

      // For documents that require signatures and have board member placeholders, inject dynamic signature blocks
      if (docReq.requiresSignature && unaData?.una_members) {
        const boardSignatureBlocks = generateBoardMemberSignatureBlocks(unaData.una_members);
        htmlContent = htmlContent.replace('<!-- BOARD_MEMBER_SIGNATURES -->', boardSignatureBlocks);
      }

      // Call edge function to generate PDF
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ html: htmlContent, author: unaData?.name || undefined }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'PDF generation failed');
      }

      const { pdf: pdfBase64, size } = await response.json();

      // Convert base64 to Blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

      // Upload to storage
      const filePath = generatedFilePath(unaData?.name, docReq.key, task.id);

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Buckets are private — store path as reference

      // Get current user session as fallback for created_by
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const createdBy = task.user_id || authSession?.user?.id || null;

      // Store document record
      const documentRecord = {
        task_id: task.id,
        una_id: task.una_id,
        document_name: docReq.title,
        document_type: 'workflow',
        file_url: filePath,
        file_path: filePath,
        file_size: size,
        status: docReq.requiresSignature ? 'draft' : 'final',
        custom_content: htmlContent,
        created_by: createdBy,
        merge_field_data: { 
          phase: 'phase_1',
          template_key: docReq.key,
          ...legacySigningMeta(docReq.key, seal.sealEnabled),
          workflow_data: workflowData 
        },
      };

      const { data: insertedDoc, error: insertError } = await supabase
        .from('generated_documents')
        .insert(documentRecord)
        .select()
        .single();

      if (insertError) {
        console.error('Database insert error:', insertError);
        throw insertError;
      }

      toast({
        title: 'Document Generated',
        description: `${docReq.title} has been created successfully.`,
      });

      await loadDocuments();
    } catch (error: any) {
      console.error('Error generating document:', error);
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate document',
        variant: 'destructive',
      });
    } finally {
      setGenerating(null);
    }
  }

  async function handleCollectSignature(doc: any) {
    setSelectedDocForSignature(doc);
    setSignatureWizardOpen(true);
  }

  async function handleFinalizeWithSignatures(signatures: any) {
    if (!selectedDocForSignature) return;

    try {
      // Fetch fresh custom_content from database
      const { data: freshDoc, error: fetchError } = await supabase
        .from('generated_documents')
        .select('custom_content, document_name, id')
        .eq('id', selectedDocForSignature.id)
        .single();

      if (fetchError) throw fetchError;
      if (!freshDoc?.custom_content) {
        throw new Error('Document HTML not found');
      }

      const finalizedTimestamp = new Date().toISOString();

      // Build signature array for Secretary Treasurer
      const signatureArray = [];
      if (signatures.treasury_secretary_signature && unaData?.treasury_secretary_id) {
        signatureArray.push({
          role: 'treasury_secretary',
          signer_id: unaData.treasury_secretary_id,
          signature_data: signatures.treasury_secretary_signature,
          signed_at: finalizedTimestamp
        });
      }

      // Embed signatures into HTML
      const signedHtml = await embedSignaturesIntoHtml(
        freshDoc.custom_content,
        signatureArray,
        finalizedTimestamp
      );

      // Generate new PDF with embedded signatures
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No authentication session');

      const pdfResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ html: signedHtml, author: unaData?.name || undefined }),
        }
      );

      if (!pdfResponse.ok) {
        const errorData = await pdfResponse.json();
        throw new Error(errorData.error || 'PDF generation failed');
      }

      const { pdf: pdfBase64, size } = await pdfResponse.json();

      // Convert to blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const pdfBlob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });

      // Upload signed PDF
      const signedFilePath = generatedFilePath(unaData?.name, `${freshDoc.document_name}_signed`, freshDoc.id);

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(signedFilePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Buckets are private — store path as reference

      // Update document record
      const { error: updateError } = await supabase
        .from('generated_documents')
        .update({
          status: 'finalized',
          finalized_at: finalizedTimestamp,
          finalized_by: task.user_id,
          file_url: signedFilePath,
          signed_file_url: signedFilePath,
          file_path: signedFilePath,
          file_size: size,
          custom_content: signedHtml,
          signature_history: {
            finalized_signatures: {
              treasury_secretary_signature: signatures.treasury_secretary_signature,
              treasury_secretary_date: signatures.treasury_secretary_signature_date,
              finalized_at: finalizedTimestamp,
            }
          }
        })
        .eq('id', selectedDocForSignature.id);

      if (updateError) throw updateError;

      setSignatureWizardOpen(false);
      setSelectedDocForSignature(null);

      toast({
        title: 'Document Signed',
        description: `${unaData?.primary_officer_role || 'Secretary Treasurer'} signature has been added. You can now request board member signatures.`,
      });

      await loadDocuments();
    } catch (error: any) {
      console.error('Error finalizing document:', error);
      toast({
        title: 'Finalization Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  }

  async function handleMarkPhaseComplete() {
    // Check at least one document generated
    if (documents.length === 0) {
      toast({
        title: 'Documents Required',
        description: 'Please generate at least one document before completing this phase.',
        variant: 'destructive',
      });
      return;
    }

    // Show success and let parent handle database updates
    toast({
      title: 'Phase 1 Complete',
      description: 'Contribution documents prepared. Proceeding to Phase 2.',
    });

    // Parent (WorkflowPhaseManager) will handle updating task_phases table
    onPhaseComplete();
  }

  function getDocumentStatus(docKey: string) {
    const doc = documents.find(d => d.merge_field_data?.template_key === docKey);
    if (!doc) return { status: 'not_generated', doc: null };
    
    // Check if finalized - these documents are complete
    if (doc.status === 'finalized') {
      return { status: 'complete', doc };
    }
    
    // Check if needs signature - only for draft documents
    const req = requiredDocuments.find(r => r.key === docKey);
    if (req?.requiresSignature && doc.status === 'draft') {
      return { status: 'needs_signature', doc };
    }
    
    return { status: 'complete', doc };
  }

  const allComplete = documents.length >= 1;

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CardTitle>Phase 1: Prepare Contribution Documents (PITCH)</CardTitle>
                <Badge>Current Phase</Badge>
              </div>
              <p className="text-muted-foreground">
                Create authorization documents from the contributor's perspective
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Steps to Complete */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Steps to Complete:</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Generate Authorization Resolution document</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>{unaData?.primary_officer_role || 'Secretary Treasurer'} signs the document</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Request board member signatures (if applicable)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Generate additional supporting documents</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Required Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Required Documents:</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <SealApplyCheckbox
              available={seal.sealAvailable}
              checked={seal.applySeal}
              onChange={seal.setApplySeal}
            />
            {requiredDocuments.map((docReq) => {
              const { status, doc } = getDocumentStatus(docReq.key);

              return (
                <div
                  key={docReq.key}
                  className="border rounded-lg p-4 flex items-start justify-between gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <h5 className="font-medium">{docReq.title}</h5>
                      {docReq.requiresSignature && doc?.status !== 'finalized' && (
                        <Badge variant="outline" className="text-xs">
                          Signature Required
                        </Badge>
                      )}
                    </div>
                    {doc && (
                      <div className="mt-2">
                        <Badge 
                          variant={doc.status === 'finalized' || status === 'complete' ? 'default' : 'secondary'}
                        >
                          {doc.status === 'finalized' ? 'Signed & Complete' :
                           status === 'complete' ? 'Generated' : 
                           status === 'needs_signature' ? 'Needs Signature' : 
                           'Generated'}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 flex-wrap justify-end">
                    {!doc ? (
                      <Button
                        onClick={() => handleGenerateDocument(docReq)}
                        disabled={generating === docReq.key}
                      >
                        {generating === docReq.key ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          'Generate'
                        )}
                      </Button>
                    ) : (
                      <>
                        {/* Draft documents requiring signatures - show Sign & Finalize */}
                        {doc.status === 'draft' && docReq.requiresSignature && (
                          <Button onClick={() => handleCollectSignature(doc)}>
                            Sign & Finalize
                          </Button>
                        )}
                        
                        {/* After Secretary Treasurer signs, show Request Member Signatures */}
                        {doc.merge_field_data?.requires_signature !== false && task.una_id && (
                          <div className="w-full">
                            <DocumentSigningControl
                              doc={doc}
                              unaId={task.una_id}
                              sealEnabled={seal.sealAvailable}
                              onChanged={loadDocuments}
                            />
                          </div>
                        )}
                        
                        {/* All generated documents - show full button set */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPreviewDocument(doc)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Preview
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await downloadDocumentById(doc.id, { underscoreFilename: true });
                              
                              toast({
                                title: 'Download Started',
                                description: 'Document is downloading...',
                              });
                            } catch (error) {
                              console.error('Download error:', error);
                              toast({
                                title: 'Download Failed',
                                description: 'Could not download document',
                                variant: 'destructive',
                              });
                            }
                          }}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedDocForEmail(doc.id);
                            setEmailWizardOpen(true);
                          }}
                        >
                          <Mail className="h-3 w-3 mr-1" />
                          Email
                        </Button>
                        
                        <DocumentUploadButton
                          documentId={doc.id}
                          documentName={doc.document_name}
                          onUploadComplete={() => loadDocuments()}
                          fileUrl={doc.file_url}
                          hasUploadedFile={doc.file_path?.includes('-uploaded.pdf')}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Phase Completion */}
      <Alert variant={allComplete ? 'default' : 'default'} className={allComplete ? 'border-green-500 bg-green-50' : ''}>
        <AlertTitle>{allComplete ? 'Ready to Complete Phase 1' : 'Complete Phase 1'}</AlertTitle>
        <AlertDescription>
          {allComplete 
            ? 'All documents have been generated. You can proceed to Phase 2.'
            : 'Generate at least one document to proceed to the next phase.'}
        </AlertDescription>
        <Button 
          onClick={handleMarkPhaseComplete}
          disabled={!allComplete || isCompleting}
          className="mt-4"
        >
          {isCompleting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Completing...
            </>
          ) : (
            'Complete Phase 1'
          )}
        </Button>
      </Alert>

      {/* Document Preview Modal */}
      {previewDocument && (
        <DocumentPreviewModal
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
          document={previewDocument}
        />
      )}

      {/* Signature Collection Modal */}
      {signatureWizardOpen && selectedDocForSignature && (
        <FinalizeWithSignatures
          documentTitle={selectedDocForSignature.document_name}
          treasurySecretaryName={unaData?.treasury_secretary_name || 'Secretary Treasurer'}
          primaryOfficerRoleLabel={unaData?.primary_officer_role || 'Secretary Treasurer'}
          onFinalize={handleFinalizeWithSignatures}
          onCancel={() => {
            setSignatureWizardOpen(false);
            setSelectedDocForSignature(null);
          }}
          requiredSigners={['treasury_secretary']}
        />
      )}

      {/* Email Wizard */}
      {emailWizardOpen && selectedDocForEmail && (
        <DocumentEmailWizard
          open={emailWizardOpen}
          onOpenChange={(open) => {
            if (!open) {
              setEmailWizardOpen(false);
              setSelectedDocForEmail(null);
            }
          }}
          taskId={task.id}
          preSelectedDocumentId={selectedDocForEmail}
          unaId={task.una_id}
        />
      )}
    </div>
  );
}
